<template>
    <div class="email-composer" id="emailComposer" v-if="visible">
        <div class="composer-header">
            <h4 style="margin: 0; color: #fff;"><i class="fas fa-envelope-open-text"></i> Enviar E-mail</h4>
            <button class="close-composer-btn" @click="hiddenComponent()">✕</button>
        </div>

        <div class="composer-form">
            <div class="form-row">
                <div class="form-group">
                    <label class="form-label" for="email-to">Para:</label>
                    <div class="email-input-container" @click="$refs.toInput.focus()">
                        <span v-for="(email, index) in toList" :key="index" class="email-badge">
                            {{ email }}
                            <button @click.stop="removeEmail('toList', index)" class="remove-badge-btn">✕</button>
                        </span>
                        <input ref="toInput" type="text" id="email-to" class="email-input-dynamic"
                        v-model="currentTo"
                        @keydown="handleKeyDown('toList', $event)"
                        @paste="handlePaste('to', $event)"
                        placeholder="Adicionar e-mail">
                    </div>
                </div>

                <div class="form-group">
                    <label class="form-label" for="email-copy">CC:</label>
                    <div class="email-input-container" @click="$refs.copyInput.focus()">
                        <span v-for="(email, index) in copyList" :key="index" class="email-badge">
                            {{ email }}
                            <button @click.stop="removeEmail('copyList', index)" class="remove-badge-btn">✕</button>
                        </span>
                        <input ref="copyInput" type="text" id="email-copy" class="email-input-dynamic"
                        v-model="currentCopy"
                        @keydown="handleKeyDown('copyList', $event)"
                        @paste="handlePaste('copy', $event)"
                        placeholder="Adicionar e-mail para cópia">
                    </div>
                </div>
            </div>

            <div class="form-group groups-row" v-if="emailGroups && emailGroups.length > 0">
                <label class="form-label">Grupos:</label>
                <div class="groups-badges-container">
                    <button
                        v-for="group in emailGroups"
                        :key="group.id"
                        class="group-badge"
                        @click="openGroupModal(group)"
                        :title="`${group.contacts.length} contato(s)`"
                    >
                        <i class="fas fa-users"></i>
                        {{ group.name }}
                        <span class="group-count">{{ group.contacts.length }}</span>
                    </button>
                </div>
            </div>

            <div class="form-group">
                <label class="form-label" for="email-subject">Assunto:</label>
                <input type="text" id="email-subject" class="form-input" v-model="email.subject">
            </div>

            <div class="form-group">
                <TextEditor
                    ref="textEditor"
                    v-model="editorBody"
                    label="Mensagem:"
                    editor-id="emailEditor"
                    @input="onEditorInput"
                />
            </div>

            <div class="composer-actions">
                <div class="left-actions">
                    <button v-if="!loading" class="btn btn-primary" style="border-radius: 5px !important;"
                        @click="sendEmail()">
                        <i class="fas fa-paper-plane"></i> Enviar
                    </button>
                    <button v-else class="btn btn-primary" style="border-radius: 5px !important;" disabled>
                        <i class="fas fa-spinner fa-pulse"></i> Enviando
                    </button>

                    <!-- Botão Mensagens Rápidas -->
                    <div class="quick-message-wrapper" v-click-outside="closeQuickMessages">
                        <button
                            class="btn btn-quick-message"
                            style="border-radius: 5px !important;"
                            @click="toggleQuickMessages()"
                            :disabled="loadingQuickMessages"
                            title="Inserir mensagem rápida"
                        >
                            <i v-if="loadingQuickMessages" class="fas fa-spinner fa-pulse"></i>
                            <i v-else class="fas fa-bolt"></i>
                            Mensagens Rápidas
                            <i class="fas fa-chevron-up" v-if="quickMessageOpen"></i>
                            <i class="fas fa-chevron-down" v-else></i>
                        </button>

                        <div class="quick-message-dropdown" v-if="quickMessageOpen">
                            <div class="quick-message-search">
                                <input
                                    ref="quickMessageSearch"
                                    type="text"
                                    v-model="quickMessageSearch"
                                    placeholder="Pesquisar mensagem..."
                                    class="quick-message-search-input"
                                >
                            </div>
                            <div class="quick-message-list">
                                <div
                                    v-if="filteredQuickMessages.length === 0"
                                    class="quick-message-empty"
                                >
                                    <i class="fas fa-inbox"></i>
                                    <span>Nenhuma mensagem encontrada.</span>
                                </div>
                                <div
                                    v-for="qm in filteredQuickMessages"
                                    :key="qm.id"
                                    class="quick-message-item"
                                    @click="insertQuickMessage(qm)"
                                >
                                    <div class="quick-message-item-name">
                                        <i class="fas fa-bolt"></i>
                                        {{ qm.name }}
                                    </div>
                                    <div class="quick-message-item-preview" v-html="qm.message"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="right-actions">
                    <div class="right-actions">
                        <button class="toolbar-btn" style="border-radius: 5px !important;" @click="attachFile()"
                            title="Anexar arquivo">
                            <i class="fas fa-paperclip"></i> Anexar Arquivo
                        </button>
                        <button
                            class="toolbar-btn" 
                            style="border-radius: 5px !important;" 
                            @click="$emit('attachPdf')"
                            title="Anexar PDF da solicitação"
                        >
                            <i class="fas fa-file-pdf"></i> Anexar PDF da Solicitação
                        </button>
                    </div>
                </div>
            </div>

            <div class="composer-attachments" v-if="email.attachments.length > 0">
                <h5>Anexos:</h5>
                <ul>
                    <li v-for="(file, index) in email.attachments" :key="index">
                        <span>
                            <i :class="getFileIcon(file.name)"></i>
                            <span>{{ textSizeLimit(file.name, 22) }}</span>
                        </span>
                        <button @click="removeAttachment(index)" class="remove-attachment-btn"
                            title="Remover anexo">✕</button>
                    </li>
                </ul>
            </div>
        </div>

        <!-- Group Modal -->
        <div class="group-modal-overlay" v-if="groupModal.visible" @click.self="closeGroupModal()">
            <div class="group-modal">
                <div class="group-modal-header">
                    <div class="group-modal-title">
                        <i class="fas fa-users"></i>
                        <span>{{ groupModal.group ? groupModal.group.name : '' }}</span>
                        <span class="group-modal-count">{{ groupModal.group ? groupModal.group.contacts.length : 0 }} contato(s)</span>
                    </div>
                    <button class="close-modal-btn" @click="closeGroupModal()">✕</button>
                </div>

                <div class="group-modal-body">
                    <table class="group-contacts-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>E-mail</th>
                                <th class="col-checkbox">
                                    <div class="checkbox-header">
                                        <span>Para</span>
                                        <input
                                            type="checkbox"
                                            class="modal-checkbox"
                                            :checked="allToChecked"
                                            :indeterminate.prop="someToChecked && !allToChecked"
                                            @change="toggleAllTo($event.target.checked)"
                                            title="Selecionar todos para Para"
                                        >
                                    </div>
                                </th>
                                <th class="col-checkbox">
                                    <div class="checkbox-header">
                                        <span>CC</span>
                                        <input
                                            type="checkbox"
                                            class="modal-checkbox"
                                            :checked="allCcChecked"
                                            :indeterminate.prop="someCcChecked && !allCcChecked"
                                            @change="toggleAllCc($event.target.checked)"
                                            title="Selecionar todos para CC"
                                        >
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(contact, index) in groupModal.selections" :key="contact.id">
                                <td>{{ contact.name }}</td>
                                <td class="email-cell">{{ contact.contact }}</td>
                                <td class="col-checkbox">
                                    <input
                                        type="checkbox"
                                        class="modal-checkbox"
                                        v-model="groupModal.selections[index].selectedTo"
                                        @change="onToChange(index)"
                                    >
                                </td>
                                <td class="col-checkbox">
                                    <input
                                        type="checkbox"
                                        class="modal-checkbox"
                                        v-model="groupModal.selections[index].selectedCc"
                                        @change="onCcChange(index)"
                                    >
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="group-modal-footer">
                    <div class="modal-footer-actions">
                        <button class="btn-modal-cancel" @click="closeGroupModal()">Cancelar</button>
                        <button
                            class="btn-modal-add"
                            @click="addGroupContacts()"
                            :disabled="selectedToCount === 0 && selectedCcCount === 0"
                        >
                            <i class="fas fa-plus"></i> Adicionar
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="free-text-modal-overlay" v-if="freeTextModal.visible" @click.self="cancelFreeText()">
            <div class="free-text-modal">
                <div class="free-text-modal-header">
                    <i class="fas fa-pencil-alt"></i>
                    <span>Preencher campos de texto livre</span>
                </div>
                <div class="free-text-modal-body">
                    <p class="free-text-hint">
                        Esta mensagem contém campos de texto livre. Preencha-os antes de inserir:
                    </p>
                    <div
                        v-for="field in freeTextModal.fields"
                        :key="field.key"
                        class="free-text-field"
                    >
                        <label class="free-text-label">{{ field.label }}</label>
                        <input
                            type="text"
                            class="free-text-input"
                            v-model="field.value"
                            :placeholder="`Digite o conteúdo para ${field.label}...`"
                            @keydown.enter="confirmFreeText()"
                        >
                    </div>
                </div>
                <div class="free-text-modal-footer">
                    <button class="btn-free-text-cancel" @click="cancelFreeText()">
                        Cancelar
                    </button>
                    <button class="btn-free-text-confirm" @click="confirmFreeText()">
                        <i class="fas fa-check"></i> Inserir mensagem
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { getFileIcon, textSizeLimit } from "../../helpers/utils";
import TextEditor from "../shared/TextEditor.vue";

export default {
    name: "EmailEditor",
    components: {
        TextEditor
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutsideHandler = (event) => {
                    if (!el.contains(event.target)) {
                        binding.value();
                    }
                };
                document.addEventListener('click', el._clickOutsideHandler);
            },
            unbind(el) {
                document.removeEventListener('click', el._clickOutsideHandler);
            }
        }
    },
    props: ["visible", "responseData", "emailGroups", "solicitationData"],
    data() {
        return {
            loading: false,
            toList: [],
            copyList: [],
            currentTo: "",
            currentCopy: "",
            editorBody: "",
            ticketProtocol: null,
            signature: "",
            email: {
                id: null,
                from: "",
                subject: "",
                body: "",
                in_reply_to: "",
                references: "",
                thread_index: "",
                reply_email: null,
                ticket_id: null,
                request_id: null,
                attachments: []
            },
            groupModal: {
                visible: false,
                group: null,
                selections: []
            },
            quickMessages: [],
            quickMessageOpen: false,
            quickMessageSearch: "",
            loadingQuickMessages: false,
            freeTextModal: {
                visible: false,
                fields: [],
                pendingMessage: '',
                resolve: null,
            },
        }
    },
    computed: {
        allToChecked() {
            return this.groupModal.selections.length > 0 &&
                this.groupModal.selections.every(s => s.selectedTo);
        },
        someToChecked() {
            return this.groupModal.selections.some(s => s.selectedTo);
        },
        allCcChecked() {
            return this.groupModal.selections.length > 0 &&
                this.groupModal.selections.every(s => s.selectedCc);
        },
        someCcChecked() {
            return this.groupModal.selections.some(s => s.selectedCc);
        },
        selectedToCount() {
            return this.groupModal.selections.filter(s => s.selectedTo).length;
        },
        selectedCcCount() {
            return this.groupModal.selections.filter(s => s.selectedCc).length;
        },
        filteredQuickMessages() {
            if (!this.quickMessageSearch) return this.quickMessages;
            const term = this.quickMessageSearch.toLowerCase();
            return this.quickMessages.filter(qm =>
                qm.name.toLowerCase().includes(term)
            );
        }
    },
    created() {
        this.getSignature();
        this.resetResponseData();
    },
    watch: {
        responseData() {
            this.resetResponseData();
        },
        quickMessageOpen(val) {
            if (val) {
                this.$nextTick(() => {
                    if (this.$refs.quickMessageSearch) {
                        this.$refs.quickMessageSearch.focus();
                    }
                });
            } else {
                this.quickMessageSearch = "";
            }
        }
    },
    methods: {
        getFileIcon,
        textSizeLimit,

        async toggleQuickMessages() {
            if (this.quickMessageOpen) {
                this.closeQuickMessages();
                return;
            }

            await this.fetchQuickMessages();
            this.quickMessageOpen = true;
        },
        closeQuickMessages() {
            this.quickMessageOpen = false;
            this.quickMessageSearch = "";
        },
        async fetchQuickMessages() {
            this.loadingQuickMessages = true;
            try {
                const groupIds = (this.emailGroups || []).map(g => g.id).filter(Boolean);

                let response;
                if (groupIds.length > 0) {
                    response = await axios.get('/solicitacoes/quick-messages/by-groups', {
                        params: { groups: groupIds }
                    });
                } else {
                    response = await axios.get('/solicitacoes/quick-messages');
                }

                this.quickMessages = response.data;
            } catch (error) {
                console.error('Erro ao carregar mensagens rápidas:', error);
                this.quickMessages = [];
            } finally {
                this.loadingQuickMessages = false;
            }
        },
        resolveSystemVariables(message) {
            const data = this.solicitationData || {};

            const map = {
                NomeCliente: data.NomeCliente,
                sector:      data.sector,
                Base:        data.Base,
                nomePonto:   data.nomePonto,
                codigoPonto: data.codigoPonto,
                cep:         data.cep,
                endereco:    data.endereco,
                status:      data.status,
                chamado:     data.chamado,
                protocolo:   data.protocolo,
                cnpj:        data.cnpj,
                updated_by:  data.updated_by,
            };

            return message.replace(/\{\{([^}]+)\}\}/g, (match, key) => {
                if (/^texto\d+$/.test(key)) return match;

                const value = map[key];
                return (value !== undefined && value !== null && value !== '')
                    ? value
                    : '---';
            });
        },

        async resolveFreeTextVariables(message) {
            const freeTextMatches = [...message.matchAll(/\{\{(texto\d+)\}\}/g)];
            if (!freeTextMatches.length) return message;

            const seen = new Set();
            const fields = [];
            for (const m of freeTextMatches) {
                const key = m[1];
                if (!seen.has(key)) {
                    seen.add(key);
                    const num = key.replace('texto', '');
                    fields.push({ key, label: `Texto ${num}`, value: '' });
                }
            }

            const filled = await new Promise((resolve) => {
                this.freeTextModal.fields       = fields;
                this.freeTextModal.pendingMessage = message;
                this.freeTextModal.resolve       = resolve;
                this.freeTextModal.visible       = true;
            });

            if (!filled) return null; // usuário cancelou

            let result = message;
            for (const field of this.freeTextModal.fields) {
                const regex = new RegExp(`\\{\\{${field.key}\\}\\}`, 'g');
                result = result.replace(regex, field.value || '---');
            }
            return result;
        },

        confirmFreeText() {
            this.freeTextModal.visible = false;
            if (this.freeTextModal.resolve) {
                this.freeTextModal.resolve(true);
                this.freeTextModal.resolve = null;
            }
        },

        cancelFreeText() {
            this.freeTextModal.visible = false;
            if (this.freeTextModal.resolve) {
                this.freeTextModal.resolve(false);
                this.freeTextModal.resolve = null;
            }
            this.freeTextModal.fields = [];
        },

        async insertQuickMessage(qm) {
            if (!this.$refs.textEditor) return;

            let resolved = this.resolveSystemVariables(qm.message);

            resolved = await this.resolveFreeTextVariables(resolved);
            if (resolved === null) return; // usuário cancelou

            const tmp = document.createElement('div');
            tmp.innerHTML = resolved;
            tmp.querySelectorAll('.qm-variable-tag').forEach(span => {
                span.replaceWith(document.createTextNode(span.dataset.variable || span.textContent));
            });
            const cleanResolved = tmp.innerHTML;

            const currentContent = this.$refs.textEditor.getContent();
            const newContent = cleanResolved + (currentContent ? '<br><br>' + currentContent : '');
            this.$refs.textEditor.setContent(newContent);
            this.editorBody = newContent;
            this.closeQuickMessages();
        },

        openGroupModal(group) {
            this.groupModal.group = group;
            this.groupModal.selections = group.contacts.map(contact => ({
                ...contact,
                selectedTo: true,
                selectedCc: false
            }));
            this.groupModal.visible = true;
        },
        closeGroupModal() {
            this.groupModal.visible = false;
            this.groupModal.group = null;
            this.groupModal.selections = [];
        },
        onToChange(index) {
            if (this.groupModal.selections[index].selectedTo) {
                this.groupModal.selections[index].selectedCc = false;
            }
        },
        onCcChange(index) {
            if (this.groupModal.selections[index].selectedCc) {
                this.groupModal.selections[index].selectedTo = false;
            }
        },
        toggleAllTo(checked) {
            this.groupModal.selections.forEach(s => {
                s.selectedTo = checked;
                if (checked) s.selectedCc = false;
            });
        },
        toggleAllCc(checked) {
            this.groupModal.selections.forEach(s => {
                s.selectedCc = checked;
                if (checked) s.selectedTo = false;
            });
        },
        addGroupContacts() {
            this.groupModal.selections.forEach(s => {
                if (s.selectedTo) this.addEmail('toList', s.contact);
                if (s.selectedCc)  this.addEmail('copyList', s.contact);
            });
            this.closeGroupModal();
        },

        addEmail(list, email) {
            const trimmedEmail = email.trim();
            if (trimmedEmail && !this[list].includes(trimmedEmail)) {
                this[list].push(trimmedEmail);
            }
        },
        removeEmail(list, index) {
            this[list].splice(index, 1);
        },
        handleKeyDown(type, event) {
            const currentModel = type === 'toList' ? 'currentTo' : 'currentCopy';
            const keyPress = [event.key, event.code];
            
            if (keyPress.includes('Enter') || keyPress.includes(',') || keyPress.includes('Tab') || keyPress.includes('Space')) {
                event.preventDefault();
                this.addEmail(type, this[currentModel]);
                this[currentModel] = '';
            } else if (keyPress.includes('Backspace') && this[currentModel] === '') {
                this[type].pop();
            }
        },
        handlePaste(type, event) {
            event.preventDefault();
            const paste = (event.clipboardData || window.clipboardData).getData('text');
            const emails = paste.split(/[,\s;]+/);
            const list = type === 'to' ? 'toList' : 'copyList';
            emails.forEach(email => this.addEmail(list, email));
        },
        hiddenComponent() {
            this.$emit("close");
        },
        validateEmail() {
            if (this.toList.length === 0) {
                Swal.fire({ type: 'error', text: "O campo 'Para' é obrigatório.", showConfirmButton: false, timer: 3000 });
                return false;
            }
            const body = this.editorBody;
            if (!body || body == '<br>') {
                Swal.fire({ type: 'error', text: "Corpo do e-mail vazio!", showConfirmButton: false, timer: 3000 });
                return false;
            }
            return true;
        },
        handleEmail() {
            try {
                const formData = new FormData();
                this.toList.forEach(email => formData.append('to[]', email));
                this.copyList.forEach(email => formData.append('copy[]', email));
                formData.append('from', this.email.from);
                formData.append('subject', this.email.subject);
                formData.append('body', this.editorBody);
                formData.append('ticket_id', this.email.ticket_id);
                formData.append('request_id', this.responseData?.request_id ?? '');
                formData.append('in_reply_to', this.email.in_reply_to);
                formData.append('references', this.email.references);
                formData.append('thread_index', this.email.thread_index);
                formData.append('reply_email', JSON.stringify(this.email.reply_email));
                formData.append('ticket_protocol', this.ticketProtocol);
                if (this.email.attachments.length > 0) {
                    for (let i = 0; i < this.email.attachments.length; i++) {
                        formData.append('attachments[]', this.email.attachments[i]);
                    }
                }
                return formData;
            } catch (error) {
                console.error(error);
                return false;
            }
        },
        sendEmail() {
            this.loading = true;
            if (!this.validateEmail()) { this.loading = false; return; }
            const formDataEmail = this.handleEmail();
            if (!formDataEmail) {
                this.loading = false;
                Swal.fire({ type: 'error', text: "Falha na formatação do e-mail. Por favor, valide todos os campos", showConfirmButton: false, timer: 3000 });
                return;
            }
            try {
                axios.post("/emails/send", formDataEmail, { headers: { 'Content-Type': 'multipart/form-data' } })
                    .then(({ data }) => {
                        this.loading = false;
                        Swal.fire({ type: 'success', text: data.message, showConfirmButton: false, timer: 3000 })
                            .then(() => { this.$emit("emailSent", data); this.resetForm(); });
                    }).catch(error => {
                        this.loading = false;
                        console.error(error);
                        Swal.fire({ icon: 'error', text: "Falha ao enviar e-mail!", showConfirmButton: false, timer: 3000 });
                    });
            } catch (error) {
                console.error(error);
                Swal.fire({ type: 'error', text: "Falha ao enviar e-mail!", showConfirmButton: false, timer: 3000 });
                this.loading = false;
            }
        },
        resetForm() {
            Object.assign(this.$data, this.$options.data.apply(this));
            this.editorBody = "";
            if (this.$refs.textEditor) this.$refs.textEditor.clearContent();
            this.resetResponseData();
            this.hiddenComponent();
        },
        resetResponseData() {
            if (!this.responseData) return;

            if (this.responseData.is_forward) {
                const fwd = this.responseData.forwarded_email;
                const subject = this.responseData.subject_email ?? "";
                let from = this.responseData.from_email ?? "";
                if (Array.isArray(from)) from = from[0];
                this.email.from = from;
                this.toList = [];
                this.copyList = [];
                this.email.id = this.responseData?.id ?? null;
                this.email.in_reply_to = "";
                this.email.ticket_id = this.responseData?.ticket_id ?? null;
                this.email.request_id = this.responseData?.request_id ?? null;
                this.email.references = null;
                this.email.thread_index = null;
                this.email.reply_email = null;
                this.email.attachments = [];
                this.ticketProtocol = this.responseData?.protocol ?? null;
                this.email.subject = subject.toLowerCase().startsWith("enc:") ? subject : `ENC: ${subject}`;
                if (fwd) {
                    const fromStr = Array.isArray(fwd.from_email) ? fwd.from_email.join("; ") : (fwd.from_email ?? "");
                    const toStr   = Array.isArray(fwd.to_email)   ? fwd.to_email.join("; ")   : (fwd.to_email ?? "");
                    const forwardedContent = `
                        <br><br>
                        <div style="border-left: 3px solid #ccc; padding-left: 12px; color: #555; margin-top: 10px;">
                            <p style="margin:0;"><strong>---------- Mensagem encaminhada ----------</strong></p>
                            <p style="margin:4px 0;"><strong>De:</strong> ${fromStr}</p>
                            <p style="margin:4px 0;"><strong>Data:</strong> ${fwd.created_at ?? ""}</p>
                            <p style="margin:4px 0;"><strong>Assunto:</strong> ${fwd.subject ?? ""}</p>
                            <p style="margin:4px 0;"><strong>Para:</strong> ${toStr}</p>
                            <br>
                            ${fwd.body ?? ""}
                        </div>`;
                    this.editorBody = `<br>${this.signature}${forwardedContent}`;
                }
                this.loadForwardedAttachments(this.responseData.forwarded_attachments ?? []);
                return;
            }

            let from = this.responseData.from_email ?? "";
            let to = this.responseData.to_email   ?? [];
            let copy = this.responseData.copy_email ?? [];
            const subject = this.responseData?.subject_email ?? "";

            if (this.responseData.is_received) {
                from = this.responseData.to_email;
                to = this.responseData.from_email;
            }
            if (Array.isArray(from)) from = from[0];
            if (!Array.isArray(to)) to = [to];
            if (!Array.isArray(copy)) copy = [copy];
            to = to.filter(e => e && e.toString().trim() !== "");
            copy = copy.filter(e => e && e.toString().trim() !== "");

            this.email.from = from;
            this.toList = to;
            this.copyList = copy;
            this.email.id = this.responseData?.id ?? null;
            this.email.in_reply_to = this.responseData?.in_reply_to ?? "";
            this.email.ticket_id = this.responseData?.ticket_id ?? null;
            this.email.request_id = this.responseData?.request_id ?? null;
            this.email.references = this.responseData?.references ?? null;
            this.email.thread_index = this.responseData?.thread_index ?? null;

            if (this.responseData?.reply_email) {
                const replyTo = this.responseData.reply_email?.to_email;
                const replyCc = this.responseData.reply_email?.copy_email;
                this.email.reply_email = {
                    from: this.responseData.reply_email?.from_email,
                    to: Array.isArray(replyTo) ? [...replyTo] : replyTo,
                    date: this.responseData.reply_email?.created_at,
                    subject: this.responseData.reply_email?.subject,
                    cc: Array.isArray(replyCc) ? [...replyCc] : replyCc,
                    body: this.responseData.reply_email?.body,
                };
            }

            this.ticketProtocol = this.responseData?.protocol ?? null;
            this.email.subject = this.responseData?.reply_email
                ? (subject.toLowerCase().includes("re:") ? subject : `Re: ${subject}`)
                : subject;
        },
        addAttachment(file) {
            this.email.attachments.push(file);
        },
        removeAttachment(index) {
            this.email.attachments.splice(index, 1);
        },
        async attachFile() {
            const input = document.createElement('input');
            input.type = 'file';
            input.multiple = true;
            input.onchange = (e) => {
                const files = Array.from(e.target.files);
                this.email.attachments = [...this.email.attachments, ...files];
            };
            input.click();
        },
        onEditorInput(content) {
            this.editorBody = content;
        },
        getSignature() {
            axios.get('my-profile')
                .then(({ data }) => {
                    if (!data || !data?.signature) return;
                    this.editorBody = `<br><br> ${data.signature}`;
                    this.signature  = `<br><br> ${data.signature}`;
                })
                .catch(error => console.error('Error loading user data:', error));
        },
        async loadForwardedAttachments(attachments) {
            if (!attachments || attachments.length === 0) return;
            for (const att of attachments) {
                try {
                    const url = `${location.origin}/emails/${att.email_id}/attachment/${att.filename}`;
                    const response = await fetch(url, {
                        credentials: 'same-origin',
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
                        }
                    });
                    if (!response.ok) { console.error(`Falha ao buscar anexo ${att.filename}: ${response.status}`); continue; }
                    const blob = await response.blob();
                    const file = new File([blob], att.filename, { type: blob.type || 'application/octet-stream' });
                    this.email.attachments.push(file);
                } catch (error) {
                    console.error(`Erro ao carregar anexo ${att.filename}:`, error);
                }
            }
        },
    }
}
</script>

<style scoped>

.left-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
}

.right-actions {
    display: flex;
    gap: 5px;
}

.form-input {
    padding: 8px 12px;
    border: 1px solid #c9c9c9;
    border-radius: 4px !important;
    font-size: 12px;
    outline: none;
}

.form-input:focus {
    border-color: #2e8b8b;
    box-shadow: 0 0 3px rgba(46, 139, 139, 0.2);
}

.form-label {
    font-size: 11px;
    font-weight: bold;
    color: #333;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 15px;
}

.email-composer {
    background: white;
    border: 2px solid #2e8b8b;
    border-radius: 8px !important;
    margin-top: 20px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    position: relative;
}

.composer-header {
    background: linear-gradient(135deg, #2e8b8b, #4a9a9a);
    color: white;
    padding: 12px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-radius: 6px 6px 0 0 !important;
}

.close-composer-btn {
    background: none;
    border: none;
    color: white;
    font-size: 18px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 5px !important;
    transition: background-color 0.3s;
}

.close-composer-btn:hover {
    background-color: #343a40;
    font-weight: bold;
}

.composer-form {
    padding: 20px;
}

.composer-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #eee;
}

.composer-attachments {
    margin-top: 15px;
    padding-top: 15px;
    border-top: 1px solid #eee;
}

.composer-attachments > h5 {
    font-size: 13px;
    margin: 0 0 10px 0;
}

.composer-attachments > ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.composer-attachments > ul > li {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background: #f1f1f1;
    padding: 4px 8px;
    border-radius: 3px;
    font-size: 12px;
}

.composer-attachments > ul > li > span {
    display: flex;
    align-items: center;
    gap: 5px;
}

.remove-attachment-btn {
    background: none;
    border: none;
    color: #dc3545;
    font-weight: bold;
    cursor: pointer;
    font-size: 14px;
    padding: 0 5px;
}

.email-input-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    border: 1px solid #c9c9c9;
    border-radius: 4px !important;
    cursor: text;
    min-height: 38px;
}

.email-input-container:focus-within {
    border-color: #2e8b8b;
    box-shadow: 0 0 3px rgba(46, 139, 139, 0.2);
}

.email-badge {
    display: inline-flex;
    align-items: center;
    background-color: #e0e0e0;
    color: #333;
    border-radius: 12px;
    padding: 3px 8px;
    font-size: 12px;
    line-height: 1;
    gap: 5px;
}

.remove-badge-btn {
    background: none;
    border: none;
    color: #777;
    font-weight: bold;
    cursor: pointer;
    font-size: 14px;
    padding: 0;
    line-height: 1;
}

.remove-badge-btn:hover { color: #000; }

.email-input-dynamic {
    flex-grow: 1;
    border: none;
    outline: none;
    padding: 5px 0;
    font-size: 12px;
    min-width: 120px;
    background: transparent;
}

/* ---- Quick Messages ---- */
.quick-message-wrapper {
    position: relative;
}

.btn-quick-message {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #f0a500, #f5b800);
    color: #fff;
    border: none;
    border-radius: 5px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(240, 165, 0, 0.3);
}

.btn-quick-message:hover:not(:disabled) {
    background: linear-gradient(135deg, #d4920a, #f0a500);
    box-shadow: 0 3px 10px rgba(240, 165, 0, 0.4);
}

.btn-quick-message:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.quick-message-dropdown {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    width: 340px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    overflow: hidden;
    animation: dropUp 0.15s ease;
}

@keyframes dropUp {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: translateY(0); }
}

.quick-message-search {
    padding: 10px;
    border-bottom: 1px solid #eee;
    background: #f8f9fa;
}

.quick-message-search-input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 12px;
    outline: none;
}

.quick-message-search-input:focus {
    border-color: #f0a500;
    box-shadow: 0 0 3px rgba(240, 165, 0, 0.25);
}

.quick-message-list {
    max-height: 260px;
    overflow-y: auto;
}

.quick-message-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px;
    color: #aaa;
    font-size: 12px;
}

.quick-message-empty i {
    font-size: 22px;
}

.quick-message-item {
    padding: 10px 14px;
    cursor: pointer;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.15s;
}

.quick-message-item:last-child {
    border-bottom: none;
}

.quick-message-item:hover {
    background: #fff8e6;
}

.quick-message-item-name {
    font-size: 12px;
    font-weight: 700;
    color: #333;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
}

.quick-message-item-name i {
    color: #f0a500;
    font-size: 11px;
}

.quick-message-item-preview {
    font-size: 11px;
    color: #777;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.4;
}

/* ---- Groups Row ---- */
.groups-row { margin-bottom: 15px; }

.groups-badges-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 4px 0;
}

.group-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: linear-gradient(135deg, #e8f5f5, #d0ecec);
    color: #2e8b8b;
    border: 1px solid #b2d8d8;
    border-radius: 20px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    line-height: 1;
    border-radius: 7px !important;
}

.group-badge:hover {
    background: linear-gradient(135deg, #2e8b8b, #4a9a9a);
    color: #fff;
    border-color: #2e8b8b;
    box-shadow: 0 2px 8px rgba(46, 139, 139, 0.3);
    transform: translateY(-1px);
}

.group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(46, 139, 139, 0.15);
    color: inherit;
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 11px;
    font-weight: 700;
    min-width: 20px;
}

.group-badge:hover .group-count { background: rgba(255, 255, 255, 0.25); }

/* ---- Group Modal ---- */
.group-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(2px);
}

.group-modal {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.22);
    width: 600px;
    max-width: 95vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalIn 0.18s ease;
}

@keyframes modalIn {
    from { opacity: 0; transform: scale(0.95) translateY(-10px); }
    to   { opacity: 1; transform: scale(1)   translateY(0); }
}

.group-modal-header {
    background: linear-gradient(135deg, #2e8b8b, #4a9a9a);
    color: #fff;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}

.group-modal-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 700;
}

.group-modal-count {
    font-size: 11px;
    font-weight: 400;
    background: rgba(255,255,255,0.2);
    padding: 2px 8px;
    border-radius: 10px;
}

.close-modal-btn {
    background: none;
    border: none;
    color: #fff;
    font-size: 18px;
    cursor: pointer;
    padding: 2px 8px;
    border-radius: 4px;
    transition: background 0.2s;
}

.close-modal-btn:hover { background: rgba(0,0,0,0.2); }

.group-modal-body { overflow-y: auto; flex: 1; padding: 0; }

.group-contacts-table { width: 100%; border-collapse: collapse; font-size: 13px; }

.group-contacts-table thead tr {
    background: #f5f8f8;
    position: sticky;
    top: 0;
    z-index: 1;
}

.group-contacts-table th {
    padding: 10px 16px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 2px solid #e0ecec;
}

.group-contacts-table td {
    padding: 10px 16px;
    border-bottom: 1px solid #f0f0f0;
    color: #333;
    vertical-align: middle;
}

.group-contacts-table tbody tr:hover { background: #f5fcfc; }
.group-contacts-table tbody tr:last-child td { border-bottom: none; }

.col-checkbox { width: 70px; text-align: center !important; }
.col-checkbox td, td.col-checkbox { text-align: center; }

.checkbox-header { display: flex; flex-direction: column; align-items: center; gap: 4px; }

.email-cell { color: #2e8b8b; font-size: 12px; }

.modal-checkbox { width: 16px; height: 16px; cursor: pointer; accent-color: #2e8b8b; }

.group-modal-footer {
    padding: 14px 20px;
    border-top: 1px solid #eee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    background: #fafafa;
}

.modal-footer-actions { display: flex; gap: 8px; }

.btn-modal-cancel {
    background: none;
    border: 1px solid #ccc;
    color: #666;
    padding: 7px 16px;
    border-radius: 5px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-modal-cancel:hover { background: #f0f0f0; border-color: #aaa; }

.btn-modal-add {
    background: linear-gradient(135deg, #2e8b8b, #4a9a9a);
    border: none;
    color: #fff;
    padding: 7px 18px;
    border-radius: 5px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
}

.btn-modal-add:hover:not(:disabled) {
    background: linear-gradient(135deg, #256f6f, #2e8b8b);
    box-shadow: 0 3px 10px rgba(46, 139, 139, 0.35);
}

.btn-modal-add:disabled { opacity: 0.5; cursor: not-allowed; }

/* ---- Free Text Modal ---- */
.free-text-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(2px);
}

.free-text-modal {
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.22);
    width: 480px;
    max-width: 95vw;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: modalIn 0.18s ease;
}

.free-text-modal-header {
    background: linear-gradient(135deg, #f0a500, #f5b800);
    color: #333;
    padding: 14px 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
}

.free-text-modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.free-text-hint {
    font-size: 12px;
    color: #666;
    margin: 0;
}

.free-text-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.free-text-label {
    font-size: 12px;
    font-weight: 700;
    color: #444;
}

.free-text-input {
    padding: 7px 10px;
    border: 1px solid #ced4da;
    border-radius: 5px;
    font-size: 13px;
    outline: none;
    transition: border-color 0.15s;
}

.free-text-input:focus {
    border-color: #f0a500;
    box-shadow: 0 0 3px rgba(240, 165, 0, 0.25);
}

.free-text-modal-footer {
    padding: 14px 20px;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
    background: #fafafa;
}

.btn-free-text-cancel {
    background: none;
    border: 1px solid #ccc;
    color: #666;
    padding: 7px 16px;
    border-radius: 5px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-free-text-cancel:hover { background: #f0f0f0; border-color: #aaa; }

.btn-free-text-confirm {
    background: linear-gradient(135deg, #f0a500, #f5b800);
    border: none;
    color: #333;
    padding: 7px 18px;
    border-radius: 5px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
}

.btn-free-text-confirm:hover {
    background: linear-gradient(135deg, #d4920a, #f0a500);
    box-shadow: 0 3px 10px rgba(240, 165, 0, 0.35);
}
</style>